import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { clearStoredSession } from "../api";

const AuthContext = createContext(null);

/**
 * Read the cached admin user from localStorage.
 * Parsing is guarded: a corrupted `admin_user` value used to throw inside the
 * state initializer and take down the whole provider, which left the portal
 * unusable until the browser storage was cleared by hand.
 */
const readStoredUser = () => {
  try {
    const raw = localStorage.getItem("admin_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    localStorage.removeItem("admin_user");
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));
  const [user, setUser] = useState(readStoredUser);
  // Only block the first paint when we hold a token but have no cached user to
  // render with. When both are cached we render immediately and revalidate in
  // the background, so a refresh never shows a loading screen.
  const [loading, setLoading] = useState(
    () => Boolean(localStorage.getItem("admin_token")) && !readStoredUser()
  );

  const clearSession = useCallback(() => {
    clearStoredSession();
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const syncUser = async () => {
      try {
        const res = await api.get("/auth/me");
        if (cancelled) return;
        setUser(res.data.user);
        localStorage.setItem("admin_user", JSON.stringify(res.data.user));
      } catch (err) {
        if (cancelled) return;
        // Only sign out when the server actively rejects the token. Network
        // failures or a restarting API must not evict a valid session.
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          clearSession();
        }
      } finally {
        // Runs on every path, so `loading` can never latch on.
        if (!cancelled) setLoading(false);
      }
    };

    syncUser();

    return () => {
      cancelled = true;
    };
  }, [token, clearSession]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("admin_token", res.data.token);
    localStorage.setItem("admin_user", JSON.stringify(res.data.user));
    setToken(res.data.token);
    setUser(res.data.user);
  };

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      isAuthenticated: Boolean(token),
      login,
      logout
    }),
    [token, user, loading, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
